// Layout Context Provider
// Reconstructed - February 9, 2026
// Extended with layoutMode for macOS Desktop UI
// Updated: Cloud OS view mode added

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type LayoutMode = 'dashboard' | 'cloudos';

interface LayoutContextType {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapse: () => void;
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
}

const LAYOUT_MODE_KEY = 'genofx-layout-mode';

function getStoredLayoutMode(): LayoutMode {
  try {
    const stored = localStorage.getItem(LAYOUT_MODE_KEY);
    if (stored === 'cloudos') return 'cloudos';
  } catch {}
  return 'dashboard';
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>(getStoredLayoutMode);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleSidebarCollapse = () => setSidebarCollapsed(!sidebarCollapsed);

  const setLayoutMode = useCallback((mode: LayoutMode) => {
    setLayoutModeState(mode);
    try { localStorage.setItem(LAYOUT_MODE_KEY, mode); } catch {}
  }, []);

  return (
    <LayoutContext.Provider
      value={{
        sidebarOpen,
        setSidebarOpen,
        toggleSidebar,
        sidebarCollapsed,
        setSidebarCollapsed,
        toggleSidebarCollapse,
        layoutMode,
        setLayoutMode,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
}

function useLayout() {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}

export function useLayoutMode() {
  const { layoutMode, setLayoutMode } = useLayout();
  return { layoutMode, setLayoutMode };
}
